import React, { useState } from 'react';
import styled from 'styled-components';
import { FiUser, FiMail, FiPhone, FiMapPin, FiPackage, FiSettings, FiBarChart2 } from 'react-icons/fi';
import Header from '../components/Header';
import PurchaseHistory from '../components/PurchaseHistory';
import StudentDashboard from '../components/StudentDashboard';

interface UserProfilePageProps {
  onHomeClick: () => void;
  onBooksClick: () => void;
  onVideoLearningClick: () => void;
  onBlogsClick: () => void;
  onFaqClick?: () => void;
  onFeedbackClick?: () => void;
  onLoginClick: () => void;
  onAdminClick: () => void;
  onMkAcademyOnlineClick: () => void;
  onMkAcademyInPersonClick: () => void;
  onNavigateToPackage?: (packageId: number) => void;
  isAuthenticated: boolean;
  isAdmin: boolean;
  user: any;
  onLogout: () => void;
}

const UserProfilePage: React.FC<UserProfilePageProps> = (props) => {
  const [activeTab, setActiveTab] = useState<'dashboard' | 'profile' | 'purchases'>('dashboard');

  if (!props.isAuthenticated) {
    return (
      <PageContainer>
        <Header {...props} />
        <ContentContainer>
          <NotLoggedIn>
            <h2>Please Log In</h2>
            <p>You need to be logged in to view your profile.</p>
            <button onClick={props.onLoginClick}>
              Go to Login
            </button>
          </NotLoggedIn>
        </ContentContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Header {...props} />
      <ContentContainer>
        <ProfileCard>
          <ProfileHeader>
            <ProfileAvatar>
              <FiUser size={48} />
            </ProfileAvatar>
            <ProfileInfo>
              <UserName>{props.user?.name || 'User'}</UserName>
              <UserEmail>{props.user?.email || ''}</UserEmail>
              <UserRole>{props.user?.role || 'Student'}</UserRole>
            </ProfileInfo>
          </ProfileHeader>

          <Tabs>
            <Tab 
              active={activeTab === 'dashboard'} 
              onClick={() => setActiveTab('dashboard')}
            >
              <FiBarChart2 /> Dashboard
            </Tab>
            <Tab 
              active={activeTab === 'profile'} 
              onClick={() => setActiveTab('profile')}
            >
              <FiUser /> Profile Information
            </Tab>
            <Tab 
              active={activeTab === 'purchases'} 
              onClick={() => setActiveTab('purchases')}
            >
              <FiPackage /> Purchase History
            </Tab>
          </Tabs>

          <TabContent>
            {activeTab === 'dashboard' ? (
              <DashboardSection>
                <StudentDashboard 
                  userId={props.user?.id} 
                  onNavigateToPackage={props.onNavigateToPackage}
                  onNavigateToPurchases={() => setActiveTab('purchases')}
                />
              </DashboardSection>
            ) : activeTab === 'profile' ? (
              <ProfileSection>
                <SectionTitle>
                  <FiSettings /> Account Information
                </SectionTitle>
                
                <InfoGrid>
                  <InfoItem>
                    <InfoLabel><FiUser /> Name</InfoLabel>
                    <InfoValue>{props.user?.name || 'Not set'}</InfoValue>
                  </InfoItem>

                  <InfoItem>
                    <InfoLabel><FiMail /> Email</InfoLabel>
                    <InfoValue>{props.user?.email || 'Not set'}</InfoValue>
                  </InfoItem>

                  <InfoItem>
                    <InfoLabel><FiPhone /> Phone</InfoLabel>
                    <InfoValue>{props.user?.phone || 'Not set'}</InfoValue>
                  </InfoItem>

                  <InfoItem>
                    <InfoLabel><FiMapPin /> Role</InfoLabel>
                    <InfoValue>{props.user?.role || 'Student'}</InfoValue>
                  </InfoItem>

                  <InfoItem>
                    <InfoLabel>Member Since</InfoLabel>
                    <InfoValue>
                      {props.user?.created_at 
                        ? new Date(props.user.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })
                        : 'Unknown'}
                    </InfoValue>
                  </InfoItem>

                  <InfoItem>
                    <InfoLabel>User ID</InfoLabel>
                    <InfoValue>#{props.user?.id || 'N/A'}</InfoValue>
                  </InfoItem>
                </InfoGrid>
              </ProfileSection>
            ) : (
              <PurchasesSection>
                <SectionTitle>
                  <FiPackage /> Your Purchase History
                </SectionTitle>
                {props.user?.id ? (
                  <PurchaseHistory userId={props.user.id} />
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                    Unable to load purchase history. User ID not found.
                  </div>
                )}
              </PurchasesSection>
            )}
          </TabContent>
        </ProfileCard>
      </ContentContainer>
    </PageContainer>
  );
};

export default UserProfilePage;

// Styled Components
const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f8f9ff 0%, #ffffff 100%);
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 100px 20px 40px;
`;

const ProfileCard = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const ProfileHeader = styled.div`
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  padding: 40px;
  display: flex;
  align-items: center;
  gap: 24px;
`;

const ProfileAvatar = styled.div`
  width: 100px;
  height: 100px;
  background: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #3b82f6;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const ProfileInfo = styled.div`
  color: white;
`;

const UserName = styled.h1`
  font-size: 32px;
  font-weight: 700;
  margin: 0 0 8px 0;
`;

const UserEmail = styled.div`
  font-size: 16px;
  opacity: 0.9;
  margin-bottom: 4px;
`;

const UserRole = styled.div`
  display: inline-block;
  padding: 4px 12px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  font-size: 13px;
  font-weight: 600;
  text-transform: capitalize;
`;

const Tabs = styled.div`
  display: flex;
  border-bottom: 2px solid #e5e7eb;
`;

const Tab = styled.button<{ active: boolean }>`
  flex: 1;
  padding: 16px 24px;
  background: ${props => props.active ? 'white' : '#f9fafb'};
  border: none;
  border-bottom: 3px solid ${props => props.active ? '#3b82f6' : 'transparent'};
  color: ${props => props.active ? '#3b82f6' : '#6b7280'};
  font-weight: ${props => props.active ? '600' : '400'};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.active ? 'white' : '#f3f4f6'};
    color: ${props => props.active ? '#3b82f6' : '#374151'};
  }
`;

const TabContent = styled.div`
  padding: 32px;
`;

const DashboardSection = styled.div``;

const ProfileSection = styled.div``;

const PurchasesSection = styled.div``;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 24px 0;
  display: flex;
  align-items: center;
  gap: 10px;

  svg {
    color: #3b82f6;
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
`;

const InfoItem = styled.div`
  padding: 20px;
  background: #f9fafb;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
`;

const InfoLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 6px;

  svg {
    color: #3b82f6;
  }
`;

const InfoValue = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: #1f2937;
`;

const NotLoggedIn = styled.div`
  max-width: 500px;
  margin: 60px auto;
  text-align: center;
  padding: 48px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  h2 {
    font-size: 24px;
    color: #1f2937;
    margin: 0 0 12px 0;
  }

  p {
    color: #6b7280;
    margin: 0 0 24px 0;
  }

  button {
    padding: 12px 32px;
    background: #3b82f6;
    color: white;
    border: none;
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;

    &:hover {
      background: #2563eb;
    }
  }
`;

